import type { CanvasNode, CanvasEdge } from "@opus-dt/shared-types";
import type { LineCanvas } from "@/types/canvas.types";
import type { MachineDetail } from "@/features/lines/hooks";
import type { DraftLine } from "@/features/lines/draft/useLineDraftState";

export const DRAFT_LINE_SENTINEL = "draft:new";

export function isDraftLineId(lineId: string): boolean {
  return lineId === DRAFT_LINE_SENTINEL;
}

export function isDraftEntityId(id: string | null | undefined): boolean {
  return !!id && id.startsWith("tmp_");
}

export function draftToLineCanvas(draft: DraftLine): LineCanvas {
  const machineLabelByTmpId = new Map<string, string>();
  for (const m of Object.values(draft.machines)) {
    machineLabelByTmpId.set(m.tmpId, m.basic.name);
  }

  const nodes: CanvasNode[] = draft.canvas.nodes.map((n) => ({
    id: n.tmpId,
    type: n.type,
    label:
      n.machineTmpId && machineLabelByTmpId.get(n.machineTmpId)
        ? machineLabelByTmpId.get(n.machineTmpId)!
        : n.label,
    x: n.x,
    y: n.y,
    data: {
      ...n.data,
      ...(n.machineTmpId ? { machineId: n.machineTmpId } : {}),
    },
  }));

  const edges: CanvasEdge[] = draft.canvas.edges.map((e) => ({
    id: e.tmpId,
    sourceNodeId: e.sourceNodeTmpId,
    targetNodeId: e.targetNodeTmpId,
    type: e.type,
    data: e.data ?? {},
  }));

  return {
    nodes,
    edges,
    viewport: null,
    isFallback: false,
  };
}

export function draftMachinesToDetails(draft: DraftLine): MachineDetail[] {
  return Object.values(draft.machines).map((m) => ({
    id: m.tmpId,
    code: m.basic.code,
    name: m.basic.name,
    type: m.basic.type,
    status: "NODATA" as const,
    statusSince: null,
    influencesStatus: false,
    requiresOperator: m.basic.requiresOperator ?? false,
    stoppedCostPerHour: null,
    // Surfacing do papel de produção p/ o badge do canvas no editor (draft).
    parameters: m.parameters.map((p) => ({
      id: p.tmpId,
      name: p.name,
      unit: "unit" in p ? p.unit : null,
      currentValue: null,
      source: p.source,
      influencesStatus: p.category === "STATUS",
      productionRole: "productionRole" in p ? (p.productionRole ?? null) : null,
    })),
  }));
}
